/** @jsxImportSource @emotion/react */
// see - https://www.figma.com/file/pv2HV6DTSKY612VWeYWYdL/CE22-Design-System?node-id=48%3A264
import type { ReactNode } from 'react';
import React, { useCallback, useRef, useState } from 'react';
import type { TooltipProps } from '@mui/material/Tooltip';
import Tooltip from '@mui/material/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import type { Interpolation } from '@emotion/serialize';
import type { Theme } from '@emotion/react';
import type { ITheme } from '../../styles/theme';
import {
  ELEMENT_ID_DATA_LOG_ATTRIBUTE,
  ELEMENT_ID_DATA_TEST_ATTRIBUTE,
} from '../../../constants/data-element-tracking-ids';
import type { IPropsWithTestId } from '../../../constants/data-element-tracking-ids';
import { ElementZIndexOrdering } from '../../utils/element-z-iIndex-ordering';
import { BoxWithId } from '../BoxWithId/BoxWithId';

interface ITooltipContentComponent {
  openTooltip: boolean;
  hiddenImmediately: boolean;
  setHiddenImmediately: React.Dispatch<React.SetStateAction<boolean>>;
  handleCloseTooltip: () => void;
  renderTooltipContent: React.FC<{
    handleCloseTooltipWithNoTransition: () => void;
  }>;
  isInHeader: boolean;
}

const TooltipContentComponent: React.FC<
ITooltipContentComponent & {
  parentElementId: IPropsWithTestId['dataElementId'];
}
> = ({
  openTooltip,
  hiddenImmediately,
  setHiddenImmediately,
  handleCloseTooltip,
  renderTooltipContent,
  isInHeader,
  parentElementId,
}) => {
  const tooltipPopoverRef = useRef<HTMLDivElement>(null);

  const handleCloseTooltipWithNoTransition = useCallback(() => {
    setHiddenImmediately(true);
    if (tooltipPopoverRef.current) {
      // We must directly modify the dom node to hide this element,
      // because if we wait for React to do it in the next rerender,
      // it will have already started to look super weird and render
      // on top of the loading overlay:
      tooltipPopoverRef.current.parentElement?.setAttribute(
        'style',
        'display: none;',
      );
    }
    handleCloseTooltip();
  }, [handleCloseTooltip, setHiddenImmediately]);

  // This fixes an issue where the css transition to
  // close the tooltip plays during page navigation.
  // The tooltip displays on top of the
  if (hiddenImmediately && !openTooltip) {
    return null;
  }

  return (
    <BoxWithId
      css={{
        display: hiddenImmediately && !openTooltip ? 'none' : 'flex',
        zIndex: isInHeader
          ? ElementZIndexOrdering.HEADER_MENU_DROPDOWN
          : undefined,
      }}
      ref={tooltipPopoverRef}
      dataElementId={`${parentElementId}-tooltip-body`}
    >
      {renderTooltipContent({ handleCloseTooltipWithNoTransition })}
    </BoxWithId>
  );
};

interface IToopltipWithLinks
  extends Pick<TooltipProps, 'componentsProps'>,
  IPropsWithTestId {
  titleProps: {
    content: ReactNode;
    additionalStyles?: Interpolation<Theme>;
    additionalIconStyles?: Interpolation<Theme>;
  };
  renderTooltipContent: React.FC<{
    handleCloseTooltipWithNoTransition: () => void;
  }>;
  openTooltip: boolean;
  setOpenTooltip: React.Dispatch<React.SetStateAction<boolean>>;
  isInHeader: boolean;
  theme: ITheme;
}

export const MenuToopltipWithLinks: React.FC<IToopltipWithLinks> = ({
  titleProps,
  renderTooltipContent,
  openTooltip,
  setOpenTooltip,
  theme,
  componentsProps,
  isInHeader,
  dataElementId,
}) => {
  const [hiddenImmediately, setHiddenImmediately] = useState(false);

  const handleCloseTooltip = useCallback(() => {
    setOpenTooltip(false);
  }, [setOpenTooltip]);

  const handleOpenTooltip = useCallback(() => {
    setHiddenImmediately(false);
    setOpenTooltip(true);
  }, [setOpenTooltip]);

  const handleToggleTooltip = useCallback(() => {
    setOpenTooltip((prev) => !prev);
  }, [setOpenTooltip]);

  const titleStyles = {
    display: 'flex',
    alignItems: 'center',
    width: 'fit-content',
    color: theme.colors.grayscale.white,
    cursor: 'pointer',
  };

  const iconStyles = {
    transition: 'all .35s ease-in-out',
  };

  const transformIconStyles = {
    transform: 'rotate(180deg)',
    transition: 'all .35s ease-in-out',
  };

  return (
    // Hover on desktops; touch on mobile devices
    <Tooltip
      title={(
        <TooltipContentComponent
          openTooltip={openTooltip}
          hiddenImmediately={hiddenImmediately}
          setHiddenImmediately={setHiddenImmediately}
          handleCloseTooltip={handleCloseTooltip}
          renderTooltipContent={renderTooltipContent}
          isInHeader={isInHeader}
          parentElementId={dataElementId}
        />
      )}
      arrow
      open={openTooltip}
      onOpen={handleOpenTooltip}
      onClose={handleCloseTooltip}
      componentsProps={componentsProps}
      placement="bottom-start"
      // By default long-pressing on mobile shows tooltip, 700ms is default value
      enterTouchDelay={0}
      leaveTouchDelay={10000}
      PopperProps={{
        disablePortal: true,
      }}
      {...{
        [ELEMENT_ID_DATA_LOG_ATTRIBUTE]: dataElementId,
        [ELEMENT_ID_DATA_TEST_ATTRIBUTE]: dataElementId,
      }}
    >
      <div
        css={[titleStyles, titleProps.additionalStyles]}
        onTouchStart={handleToggleTooltip}
      >
        {titleProps.content}
        <FontAwesomeIcon
          icon={faChevronDown}
          css={[
            iconStyles,
            titleProps.additionalIconStyles,
            openTooltip ? transformIconStyles : '',
          ]}
        />
      </div>
    </Tooltip>
  );
};
